<template>
    <section>
        <!-- B: 그룹만 선택된 상태 — 일괄 다운로드. 기존 단건 화면(A)은 그대로 둔다. -->
        <b-row v-if="isBulkMode">
            <b-col :md="12">
                <BulkDownloadForm
                    :groupIdx="groupIdx"
                    :accounts="groupAccounts"
                    :userEmail="userEmail"
                    :jobs="bulkJobs"
                    @submit="handleBulkSubmit"
                />
            </b-col>
        </b-row>

        <b-row v-else>
            <b-col
                :md="12"
            >
                <b-overlay
                    :show="statusSummary === 'loading'"
                >
                    <b-card
                        no-body
                    >
                        <b-alert
                            :show="isLimitOver"
                            variant="warning"
                            class="p-1 mb-0"
                        >
                            <i class="fas fa-exclamation-circle"></i>
                            {{ $t('v4["Please wait for loading..."]') }}
                        </b-alert>
                        <b-table-simple
                            responsive
                            class="m-0"
                        >
                            <colgroup>
                                <col>
                                <col>
                                <col>
                                <col width="150px">
                            </colgroup>
                            <b-thead>
                                <b-tr>
                                    <b-th>
                                        {{ $t('v4.Usage Month') }}
                                    </b-th>
                                    <b-th>
                                        {{ $t('v4.Type') }}
                                    </b-th>
                                    <b-th
                                        class="text-right"
                                    >
                                        {{ $t('v4.Total (USD)') }}
                                    </b-th>
                                    <b-th
                                        class="text-center"
                                    >
                                        {{ $t('v4.Download') }}
                                    </b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody v-if="noData">
                                <b-tr>
                                    <b-td
                                        class="pb-50 pt-2 text-center"
                                        colspan="4"
                                    >
                                        {{ $t("org.message.No Data") }}
                                    </b-td>
                                </b-tr>
                            </b-tbody>
                            <b-tbody v-else>
                                <template
                                    v-for="(item, index) in invoiceSummary"
                                >
                                    <template
                                        v-for="(subItem, subIndex) in item.detail"
                                    >
                                        <b-tr
                                            v-if="subIndex === 0"
                                            :key="`${index}-${subIndex}`"
                                        >
                                            <b-td
                                                :rowspan="item.detail.length"
                                            >
                                                {{ item.month }}
                                            </b-td>
                                            <b-td
                                                :class="subItem.type === 'Anniversary' ? 'table-primary text-black-50' : 'table-success'"
                                            >
                                                {{ subItem.type }}
                                            </b-td>
                                            <b-td
                                                class="text-right"
                                            >
                                                {{ $_getDollar(subItem.usdCost) }}
                                            </b-td>
                                            <b-td
                                                :rowspan="item.detail.length"
                                                class="text-center"
                                            >
                                                <!--
                                                    일괄 다운로드 목록(BulkJobList)과 같은 버튼을 쓴다.
                                                    같은 화면에서 모드만 바뀌는데 생김새가 달랐다(BO-980).

                                                    진행 중 disabled 는 표시가 아니라 방지다 — 예전 <a> 는
                                                    아이콘만 스피너로 바뀌고 클릭은 계속 먹어서, 연타하면
                                                    같은 PDF 생성 요청이 그만큼 더 나갔다.
                                                -->
                                                <b-button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    class="text-nowrap"
                                                    :disabled="pdfStatus.includes(item.month)"
                                                    @click="downloadInvoicePDF(item.month)"
                                                >
                                                    <b-spinner
                                                        v-if="pdfStatus.includes(item.month)"
                                                        small
                                                        class="mr-25"
                                                    />
                                                    {{ $t('v4.Download') }}
                                                </b-button>
                                            </b-td>
                                        </b-tr>
                                        <b-tr v-else
                                            :key="`${index}-${subIndex}`"
                                        >
                                            <b-td
                                                :class="subItem.type === 'Anniversary' ? 'table-primary' : 'table-success'"
                                            >
                                                {{ subItem.type }}
                                            </b-td>
                                            <b-td
                                                class="text-right"
                                            >
                                                {{ $_getDollar(subItem.usdCost) }}
                                            </b-td>
                                        </b-tr>
                                    </template>
                                </template>
                            </b-tbody>
                        </b-table-simple>
                    </b-card>
                </b-overlay>
            </b-col>
        </b-row>

        <!--
            요청 내역은 일괄 모드에서만 보인다.

            처음에는 단건 화면에서도 노출했다 — 생성이 분~시간 단위라 걸어두고 옮겨가도
            진행 상황을 보게 하려는 의도였다. 그런데 프로젝트를 고른 사용자에게는
            지금 보는 계정과 무관한 목록이 따라붙는 셈이라 오히려 혼란스럽다(BO-964 리뷰).
            일괄 요청을 건 사용자는 그룹만 다시 고르면 바로 목록으로 돌아온다.
        -->
        <b-row
            v-if="isBulkMode"
            class="mt-1"
        >
            <b-col :md="12">
                <BulkJobList />
            </b-col>
        </b-row>
    </section>
</template>

<script>
import { toRefs, ref, getCurrentInstance, computed, watch } from '@vue/composition-api'
import bus from '@/libs/bus'
import { BULK_DOWNLOAD_ENABLED } from '@/utils/featureFlags'
import BulkDownloadForm from './BulkDownloadForm.vue'
import BulkJobList from './BulkJobList.vue'
const STORE_KEY = '$_invoice'
export default {
    name: 'InvoicesModule',
    components: {
        BulkDownloadForm,
        BulkJobList
    },
    props: [
        'project',
        'yearOptions'
    ],
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store

        const { project, yearOptions } = toRefs(props)
        const selectedYear = computed(() => store.getters[`${STORE_KEY}/selectedYear`])
        const pdfStatus = ref([])
        const disabeldYearRadio = computed(() => pdfStatus.value.length > 0)
        const invoiceSummary = computed(() => store.getters[`${STORE_KEY}/summary`])
        const noData = computed(() => !yearOptions.value.length)
        const locale = computed(() => vm.$i18n.locale)
        const statusSummary = computed(() => store.getters[`${STORE_KEY}/statusSummary`])
        const MAX_LIMIT = 3
        const isLimitOver = computed(() => pdfStatus.value.length > MAX_LIMIT)
        const downloadInvoicePDF = (month) => {
            if (pdfStatus.value.length > MAX_LIMIT) {
                return
            }
            const usageAccountId = project.value.usageAccountId
            if (!pdfStatus.value.includes(month)) {
                pdfStatus.value.push(month)
            }
            store.dispatch('$_invoice/downloadInvoicePDF', { usageAccountId, month, locale: locale.value }).then(data => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', `${usageAccountId}_${month}.pdf`)
                document.body.appendChild(fileLink)
                fileLink.click()
            }).catch(() => {
                // 실패해도 사용자는 다시 눌러볼 수 있어야 한다.
                bus.$emit('request:error', {
                    title: 'ERROR',
                    content: vm.$t('v4.Request failed')
                })
            }).finally(() => {
                /*
                 * 성공·실패 어느 쪽이든 진행 표시를 반드시 걷는다.
                 *
                 * 원래는 then 안에서만 걷어서, 실패하면 이 월이 pdfStatus 에 영원히 남았다.
                 * 아이콘만 계속 돌던 시절에는 눈에 덜 띄었지만 버튼을 disabled 로 묶은
                 * 지금은 그대로 두면 새로고침 전까지 재시도가 막힌다(BO-980).
                 */
                pdfStatus.value = pdfStatus.value.filter(value => value !== month)
            })
        }

        async function getInvoiceMonths (usageAccountId) {
            try {
                await store.dispatch('$_invoice/getInvoiceMonths', { usageAccountId })
                if (yearOptions.value.length) return true
                return false
            } catch (error) {
                return false
            }
        }

        async function getInvoiceAnnualSummary (usageAccountId, year) {
            try {
                await store.dispatch('$_invoice/getInvoiceAnnualSummary', {
                    usageAccountId,
                    year
                })
                return true
            } catch (error) {
                return false
            }
        }

        watch(selectedYear, (value, oldValue) => {
            getInvoiceAnnualSummary(project.value.usageAccountId, value)
        })

        watch(project, (value, oldValue) => {
            // 일괄 모드에서는 계정이 없으므로 단건 조회를 돌리지 않는다.
            if (!value?.usageAccountId) return
            getInvoiceMonths(value.usageAccountId).then((result) => {
                if (result) {
                    getInvoiceAnnualSummary(value.usageAccountId, yearOptions.value[0])
                }
            })
        })

        // ------------------------------------------------------------------
        // 일괄 다운로드 (B)
        // ------------------------------------------------------------------

        const selectedGroup = computed(() => store.getters.selectedGroup)
        const selectedProject = computed(() => store.getters.selectedProject)
        const groupProjectMap = computed(() => store.getters.groupProjectMap)
        const bulkJobs = computed(() => store.getters[`${STORE_KEY}/bulkJobs`] || [])

        const groupIdx = computed(() => selectedGroup.value?.groupIdx ?? null)

        /**
         * 일괄 다운로드는 플래그가 켜진 테넌트에만 배포된다.
         * 꺼진 테넌트는 백엔드 접수가 거부되므로, 화면 전환 자체를 막아
         * 신규 엔드포인트를 아예 부르지 않는다.
         */
        const bulkSupported = BULK_DOWNLOAD_ENABLED

        /**
         * 그룹만 선택되고 계정이 비어 있으면 일괄 모드.
         * 프로젝트 셀렉터에서 계정을 고르거나 해제하는 것만으로 전환된다(별도 토글 없음).
         */
        const isBulkMode = computed(() =>
            bulkSupported && !!groupIdx.value && !selectedProject.value?.usageAccountId)

        /** 계정 목록은 그룹 선택 시 이미 확보된 정보를 쓴다. 사전 조회 API 가 없다. */
        const groupAccounts = computed(() => {
            if (!groupIdx.value) return []
            return (groupProjectMap.value?.[groupIdx.value] || [])
                .filter(item => item.usageAccountId)
        })

        const userEmail = computed(() => store.getters.authUserInfo?.email || '')

        const loadBulkContext = () => {
            if (!groupIdx.value) return
            store.dispatch(`${STORE_KEY}/getBulkLatestMonth`, { groupIdx: groupIdx.value }).catch(() => {})
            store.dispatch(`${STORE_KEY}/getBulkJobs`).catch(() => {})
        }

        watch(isBulkMode, (value) => {
            if (value) loadBulkContext()
        }, { immediate: true })

        watch(groupIdx, () => {
            if (isBulkMode.value) loadBulkContext()
        })

        const handleBulkSubmit = ({ groupIdx: gi, payload }) => {
            store.dispatch(`${STORE_KEY}/requestBulkDownload`, { groupIdx: gi, payload })
                .then(() => {
                    bus.$emit('request:success', {
                        title: 'SUCCESS',
                        content: vm.$t('v4.Request has been received')
                    })

                    // 접수 직후 목록을 다시 받아 새 요청이 바로 보이게 한다.
                    store.dispatch(`${STORE_KEY}/getBulkJobs`).catch(() => {})
                })
                .catch((error) => {
                    // 서버가 거부 사유(권한·중복·기간)를 문장으로 내려주므로 그대로 보여준다.
                    bus.$emit('request:error', {
                        title: 'ERROR',
                        content: error?.response?.data?.errorMessage || vm.$t('v4.Request failed')
                    })
                })
        }

        return {
            statusSummary,
            noData,
            pdfStatus,
            isLimitOver,
            disabeldYearRadio,
            invoiceSummary,
            downloadInvoicePDF,
            isBulkMode,
            groupIdx,
            groupAccounts,
            userEmail,
            bulkJobs,
            handleBulkSubmit
        }
    }
}
</script>

<style lang="scss" scoped>
.year-radio-group {
    ::v-deep > .custom-control {
    margin-bottom: 0.75rem;
  }
}
</style>
